import React, { useRef, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCoverflow, Keyboard } from "swiper/modules";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useHomeContext } from "../../../context/Home_context";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "../../home/homespeakers/HomeSpeakers.css";

const EditionSpeakers = ({ data }) => {
  const { edition_expo_data } = useHomeContext();
  const expoData = data || edition_expo_data || {};
  const swiperRef = useRef(null);

  const sliderBanners = expoData?.sliderbanners || [];

  const rawSpeakersList = useMemo(() => {
    if (!sliderBanners || sliderBanners.length === 0) return [];
    return sliderBanners.map((item) => ({
      id: item.id,
      name: item.name,
      role: item.designation,
      place: item.description,
      ring: "var(--brand-blue-light)",
      image: item.image_full_path || item.image,
    }));
  }, [sliderBanners]);

  const displaySpeakersList = useMemo(() => {
    if (rawSpeakersList.length === 0) return [];
    if (rawSpeakersList.length === 1) return rawSpeakersList;

    let list = [...rawSpeakersList];
    while (list.length < 8) {
      list = [...list, ...rawSpeakersList];
    }
    return list;
  }, [rawSpeakersList]);

  if (!rawSpeakersList || rawSpeakersList.length === 0) return null;

  return (
    <section className="speaker-section" id="advisory-committee">
      {/* Stylish Architectural Wave Ribbons & Floating Geometric Elements */}
      <div className="sp-design-bg-wrapper" aria-hidden="true">
        <div className="sp-design-gradient-base" />

        {/* Floating Glass Geometric Shapes */}
        <div className="sp-float-shape shape-1" />
        <div className="sp-float-shape shape-2" />
        <div className="sp-float-shape shape-3" />
        <div className="sp-float-shape shape-4" />

        {/* Tech Grid Pattern */}
        <div className="sp-tech-grid-pattern" />
      </div>

      <div className="speaker-header">
        <span className="speaker-eyebrow">
          <span className="pulse-dot" aria-hidden="true" />
          Bharat BuildCon Summit{" "}
          <span className="notranslate" translate="no">
            {expoData?.name
              ? expoData.name.match(/\d{4}/)?.[0] || expoData.name
              : "Edition"}
          </span>
        </span>
        <h2 className="speaker-title">Voices that powered the conversation</h2>
        <p className="speaker-sub">
          Dignitaries and leaders who shared their expertise in this edition.
        </p>
      </div>

      <div className="speaker-slider-wrap">
        {rawSpeakersList.length === 1 ? (
          <div className="speaker-few-grid">
            {rawSpeakersList.map((sp, i) => (
              <article
                key={sp.id || `${sp.name}-${i}`}
                className="speaker-card speaker-few-card is-active"
              >
                <div className="speaker-img-box">
                  <img
                    src={sp.image}
                    alt={sp.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80";
                    }}
                  />
                </div>
                <div className="speaker-info">
                  <h3 className="speaker-name">{sp.name}</h3>
                  {sp.role && <p className="speaker-role">{sp.role}</p>}
                  {sp.place && <p className="speaker-place">{sp.place}</p>}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <>
            <button
              type="button"
              className="sp-arrow-btn sp-arrow-prev"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous speaker"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className="sp-arrow-btn sp-arrow-next"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next speaker"
            >
              <FaChevronRight />
            </button>
            <Swiper
              modules={[Autoplay, EffectCoverflow, Keyboard]}
              onSwiper={(s) => (swiperRef.current = s)}
              effect="coverflow"
              grabCursor
              centeredSlides
              loop
              keyboard={{ enabled: true }}
              slidesPerView={1.15}
              spaceBetween={24}
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 140,
                modifier: 1.4,
                slideShadows: false,
              }}
              autoplay={{
                delay: 3200,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                640: { slidesPerView: 2.1 },
                980: { slidesPerView: 3.2 },
                1280: { slidesPerView: 4 },
              }}
              className="speaker-swiper"
            >
              {displaySpeakersList.map((sp, i) => (
                <SwiperSlide key={`${sp.id}-${i}`} className="speaker-slide">
                  {({ isActive }) => (
                    <article
                      className={`speaker-card ${isActive ? "is-active" : ""}`}
                      onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
                      onMouseLeave={() => swiperRef.current?.autoplay?.start()}
                    >
                      <div className="speaker-img-box">
                        <img
                          src={sp.image}
                          alt={sp.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80";
                          }}
                        />
                      </div>
                      <div className="speaker-info">
                        <h3 className="speaker-name">{sp.name}</h3>
                        {sp.role && <p className="speaker-role">{sp.role}</p>}
                        {sp.place && <p className="speaker-place">{sp.place}</p>}
                      </div>
                    </article>
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        )}
      </div>
    </section>
  );
};

export default EditionSpeakers;
